'use client';

import { MfaDialog } from '@/components/dialogs/mfa-dialog/mfa-dialog';
import { Toaster } from '@/components/ui/sonner/sonner';
import { ShowStyles } from '@/enums/show-styles';
import { useLayoutContext } from '@/hooks/state/layout';
import { clientErrorLoggerApiService } from '@/services/(frontend)/client-error-logger-api.service';
import { Configurations } from '@/types/configurations';
import { FeatureFlags } from '@/types/feature-flags';
import { SharedState } from '@/types/shared-state';
import clsx from 'clsx';
import jquery from 'jquery';
import { Work_Sans } from 'next/font/google';
import { CSSProperties, useEffect, useState } from 'react';
import { Footer } from './footer/footer';
import { Header } from './header/header';
import { KioskHeader } from './header/kiosk-header/kiosk-header';
import { Providers } from './providers';

declare global {
  // Note: Using 'interface' here to allow declaration merging as using 'type' would cause an error (Duplicate identifier 'Window').
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    $: typeof jquery;
    jQuery: typeof jquery;
  }
}

const workSans = Work_Sans({
  fallback: ['sans-serif'],
  subsets: ['latin'],
  variable: '--font-work-sans',
});

type ClientLayoutProps = {
  analyticsScriptSrc: string;
  appInsightsConnectionString?: string;
  children: React.ReactNode;
  config: Configurations;
  initialFeatureFlags: FeatureFlags;
  initialHideLayout: boolean;
  initialSharedState: SharedState;
  mobileBearerToken?: string;
};

export const ClientLayout = ({
  analyticsScriptSrc,
  appInsightsConnectionString,
  children,
  config,
  initialFeatureFlags,
  initialHideLayout,
  initialSharedState,
  mobileBearerToken,
}: ClientLayoutProps): React.JSX.Element => {
  if (typeof window !== 'undefined') {
    window.$ = window.jQuery = jquery;
  }

  useEffect(() => {
    if (appInsightsConnectionString) {
      clientErrorLoggerApiService.initialize(appInsightsConnectionString);
    }
  }, [appInsightsConnectionString]);

  return (
    <Providers analyticsScriptSrc={analyticsScriptSrc}
      config={config}
      initialFeatureFlags={initialFeatureFlags}
      initialHideLayout={initialHideLayout}
      initialSharedState={initialSharedState}
      mobileBearerToken={mobileBearerToken}
    >
      <ClientLayoutContent>
        {children}
      </ClientLayoutContent>
    </Providers>
  );
};

const ClientLayoutContent = ({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element => {
  const [headerHeight, setHeaderHeight] = useState(0);
  const { data: layoutContext } = useLayoutContext();
  const isKioskMode = layoutContext?.showStyles === ShowStyles.Kiosk;
  const mainStyles: CSSProperties & Record<string, string> | undefined = headerHeight > 0
    ? { ['--header-height']: `${headerHeight}px` }
    : undefined;

  return (
    <>
      {isKioskMode
        ? <KioskHeader onHeaderHeightChange={setHeaderHeight} />
        : !layoutContext?.hideLayout && <Header onHeaderHeightChange={setHeaderHeight} />
      }
      <main className={clsx(workSans.className,
        (!layoutContext?.showStyles || layoutContext.showStyles === ShowStyles.Main) && 'main',
        layoutContext?.showStyles === ShowStyles.MainTightHeader && 'main tight-header',
        layoutContext?.showStyles === ShowStyles.MainFullBleed && 'main tight-header full-bleed',
        layoutContext?.showStyles === ShowStyles.NoHeaders && 'no-headers',
        isKioskMode && 'main')}
      style={mainStyles}
      >
        {children}
      </main>
      <Toaster headerHeight={headerHeight}
        visibleToasts={Number(process.env.VISIBLE_TOASTS) || 1}
      />
      <MfaDialog />
      {!isKioskMode && !layoutContext?.hideLayout && <Footer />}
    </>
  );
};
